import { Box, Grid } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useEffect, useState, useCallback } from 'react';
import {
  getArrangementsPage,
  ColumnSettings,
  getJobCountByFeeArrangementId,
  getFeeArrangementById,
  createNewFeeArrangement,
  FeeArrangementDTO,
} from 'wisestep-api';
import {
  getDateUsingFormat,
  EmptyTeamsAndUsersIcon,
  WsListingLoader,
  WsButton,
  FieldOption,
  WsDataGrid,
  WsMoreOptionButton,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext';
import { defaultQuery } from '../../../../features/jobs/listing/JObSearchFilterDefaultQuery';
import { useClientAuthz, ClientAction } from '../../../../hooks/useClientAuthz';
import { useDrawer } from '../../../../hooks/useDrawer';
import { Page } from '../../../../types/Page';
import { getCurrencySymbol } from '../../../../utils';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { failed, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../utils/openUrlInNewTab';
import { StyledGrid } from '../../../clientListingPage/components/ClientList';
import {
  fetchColumnSettings,
  saveColumnSettingsToDB,
} from '../../../clientListingPage/helper/ClientListingHelper';
import { clientsAmplitudeEvents, IClientDetailsTabs } from '../../helper/clientDetailsHelper';
import { FeeArrangementListing } from '../../types/FeeArrangementListing';
import AddEntityButton from '../addEntityButton/AddEntityButton';

import AddEditFeeArrangement from './AddEditFeeArrangement';

export enum FeeArrangementFeeType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED = 'FIXED',
}

interface IJobCount {
  feeArrangementId: string;
  jobCount: number;
}

const ContractArrangementsTab = ({ clientId, clientName }: IClientDetailsTabs) => {
  const [arrangementsPage, setArrangementsPage] = useState<Page<FeeArrangementListing>>();
  const [page, setPage] = useState(0);
  const tableData: Record<string, unknown>[] = [];
  const workspace = useWorkspaceContext();
  const ArchivedTableData: Record<string, unknown>[] = [];
  const [pageSize, setPageSize] = useState<number>(25);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'fee', sort: 'asc' }]);
  const [openDrawer, closeDrawer] = useDrawer();
  const [loader, setLoader] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const isAuthorizedAddInvoice = useClientAuthz(ClientAction.ADD_FEE_ARRANGEMENTS);
  const isAuthorizedEditInvoice = useClientAuthz(ClientAction.EDIT_FEE_ARRANGEMENTS);
  const [feeArrangementIds, setFeeArrangementIds] = useState<string[]>([]);
  const [jobCount, setJobCount] = useState<IJobCount[]>([]);
  const [refresh, setRefresh] = useState<boolean>(true);

  const fetchArrangementsPage = useCallback(
    async (pageNumber) => {
      try {
        setLoader(true);
        const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
        const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
        const page = (await getArrangementsPage(pageNumber, pageSize, clientId, sortBy, sortOrder))
          .data;
        setArrangementsPage(page);
        setFeeArrangementIds(page.content.map((item) => item.id));
      } catch {
        setNotification(failed('Failed to fetch Fee Arrangements!'));
      } finally {
        setLoader(false);
      }
    },
    [clientId, pageSize, setNotification, sortModel]
  );

  useEffect(() => {
    fetchArrangementsPage(page);
  }, [fetchArrangementsPage, page, refresh]);

  useEffect(() => {
    const getJobCount = async () => {
      try {
        const response = await getJobCountByFeeArrangementId(feeArrangementIds);
        setJobCount(response.data);
      } catch (error) {
        setNotification(failed('Failed to fetch Job Count!'));
      }
    };
    getJobCount();
  }, [feeArrangementIds, setNotification]);

  const findJobCountById = (id) => {
    const obj = jobCount.find((item) => item.feeArrangementId === id);
    return obj ? obj.jobCount : 0;
  };

  useEffect(() => {
    fetchColumnSettings(ColumnSettings.CLIENT_FEEARRANGEMENT, [
      'feeArrangementLevelName',
      'action',
    ]).then((res) => res && setSettings(res));
  }, []);

  const handleSaveArrangement = () => {
    closeDrawer();
    page === 0 ? fetchArrangementsPage(0) : setPage(0);
  };
  const handleCloseDrawer = () => closeDrawer();

  const mapTableData = (clientData: FeeArrangementListing) => {
    return {
      id: clientData.id,
      clientName: clientName,
      fee: clientData.fee,
      feeType: clientData.feeType,
      feeArrangementLevelName: clientData?.feeArrangementLevel,
      startDate:
        clientData?.startDate != undefined
          ? getDateUsingFormat(clientData.startDate?.toString(), 'DD-MMM-YY')
          : '',
      endDate:
        clientData?.endDate != undefined
          ? getDateUsingFormat(clientData.endDate?.toString(), 'DD-MMM-YY')
          : '',
      employmentGuarantee:
        clientData.contractType === 'PERMANENT'
          ? `${clientData.employmentGuarantee}  ${clientData.empGuaranteePeriodUnit}`
          : '',
      clientContact: clientData.clientContact,
      contractType: clientData.contractType,
      currency: clientData.currencySymbol,
      feeArrangementFiles: clientData.fileCount,
      organizationEntityName: clientData.organizationEntityName,
      invoiceTerms: clientData.invoiceTerms,
      paymentTerms: clientData.paymentTerms,
      noticePeriod: clientData.noticePeriod,
      comments: clientData.comments,
      jobCount: findJobCountById(clientData.id),
      isArchive: clientData.isArchive,
    };
  };

  arrangementsPage?.content
    .filter((clientData) => {
      return !clientData.isArchive || clientData.isArchive === null;
    })
    .map((clientData) => {
      tableData.push(mapTableData(clientData));
    });

  arrangementsPage?.content
    .filter((clientData) => {
      return clientData.isArchive;
    })
    .map((clientData) => {
      ArchivedTableData.push(mapTableData(clientData));
    });

  const navigateToJobsListingPage = (id: string) =>
    openUrlInNewTab(
      `/jobs?d=${getEncodedQueryParams({
        ...defaultQuery,
        feeArrangementId: id,
      })}`
    );

  const handelArchive = async (id: string, isArchive: boolean) => {
    try {
      setLoader(true);
      const response = await getFeeArrangementById(id);
      response.data && archiveFeeArrangement(response.data, isArchive);
    } catch (error) {
      setNotification(warning('Unable to fetch fee arrangement!'));
    } finally {
      setLoader(false);
    }
  };

  const archiveFeeArrangement = async (arrangement: FeeArrangementDTO, isArchive: boolean) => {
    try {
      await createNewFeeArrangement({ ...arrangement, isArchive: isArchive });
      setRefresh(!refresh);
    } catch (error) {
      setNotification(warning('Unable to archive fee arrangement!'));
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'feeArrangementLevelName',
      headerName: 'Position Category',
      flex: 1,
    },
    {
      field: 'contractType',
      headerName: 'Arrangement Type',
      flex: 1,
    },
    {
      field: 'jobCount',
      headerName: 'Job Count',
      flex: 1,
      renderCell: (params) => (
        <WsButton
          onClick={() => params.value > 0 && navigateToJobsListingPage(`${params.id}`)}
          variant="text"
        >
          {params.value}
        </WsButton>
      ),
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 1,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      flex: 1,
    },
    {
      field: 'fee',
      headerName: 'Service Fees',
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return row.feeType == FeeArrangementFeeType.PERCENTAGE
          ? `${row.fee}%`
          : `${getCurrencySymbol(row.currency) ?? ''} ${row.fee ?? ''}`;
      },
    },
    {
      field: 'clientContact',
      headerName: 'Client Contact',
      flex: 1,
      renderCell: (params) => {
        const feeArr = params.row;
        return feeArr.clientContact;
      },
    },
    {
      field: 'comments',
      headerName: 'Comments',
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Edit',
      sortable: false,
      flex: 0.2,
      renderCell: (param: GridRenderCellParams<FeeArrangementListing>) => {
        return (
          <>
            {isAuthorizedEditInvoice && (
              <WsMoreOptionButton
                onItemClick={(option) => onTableMoreOptionClick(option, param)}
                items={getMoreOptions(param)}
              />
            )}
          </>
        );
      },
    },
  ];
  const getMoreOptions = (param: GridRenderCellParams<FeeArrangementListing>): string[] => {
    const baseOptions = ['Edit'];

    if (param.row.isArchive) {
      baseOptions.push('Unarchive');
    } else {
      baseOptions.push('Archive');
    }

    return baseOptions;
  };

  const onTableMoreOptionClick = (
    option: string,
    params: GridRenderCellParams<FeeArrangementListing>
  ) => moreOptionHandlers.get(option)?.(params);

  const moreOptionHandlers = new Map<
    string,
    (param: GridRenderCellParams<FeeArrangementListing>) => void
  >([
    [
      'Edit',
      (param) => {
        clientsAmplitudeEvents(
          'client_details_fee_arrangement_edit',
          'Client Details Fee Arragement Edit',
          workspace
        );
        if (param.row.jobCount === 0) {
          openDrawer({
            children: (
              <AddEditFeeArrangement
                id={param.row.id}
                clientId={clientId}
                clientName={clientName}
                onCancel={handleCloseDrawer}
                onSave={handleSaveArrangement}
              />
            ),
            size: 'small',
          });
        } else {
          setNotification(
            warning(
              "Can't edit the fee arrangement as there are jobs already created with the fee arrangement!"
            )
          );
        }
      },
    ],
    [
      'Unarchive',
      (param) => {
        handelArchive(param.row.id, false);
      },
    ],
    [
      'Archive',
      (param) => {
        handelArchive(param.row.id, true);
      },
    ],
  ]);

  const defaultColumnSettings = () =>
    columns.map((col) => ({
      id: col.field,
      label: col.headerName || col.field,
      checked: true,
    }));
  const [settings, setSettings] = useState<FieldOption[]>(defaultColumnSettings);

  const onAddButtonClick = () => {
    clientsAmplitudeEvents(
      'client_details_fee_arrangement_add',
      'Client Details Fee Arragement Add',
      workspace
    );
    openDrawer({
      children: (
        <AddEditFeeArrangement
          clientId={clientId}
          clientName={clientName}
          onCancel={handleCloseDrawer}
          onSave={handleSaveArrangement}
        />
      ),
      size: 'small',
    });
  };

  const onSortModelChange = (newSortModel: GridSortModel) => {
    setPage(0);
    setSortModel(newSortModel);
  };

  const onColumnSettingsChange = (data: FieldOption[]) => {
    saveColumnSettingsToDB(data, ColumnSettings.CLIENT_FEEARRANGEMENT, setNotification);
    setSettings(data);
  };

  return (
    <StyledGrid item xs={12} mt={2}>
      {arrangementsPage && arrangementsPage.content.length > 0 && !loader ? (
        <>
          {isAuthorizedAddInvoice && (
            <AddEntityButton
              onClick={onAddButtonClick}
              justifyContent="flex-end"
              buttonText="Add Arrangement"
            />
          )}
          {settings != null && (
            <Grid xs={12} sx={{ backgroundColor: 'white' }} mt={1}>
              <WsDataGrid
                autoHeight
                columns={columns}
                rows={tableData}
                onPageChange={setPage}
                pageSize={pageSize}
                page={page}
                onPageSizeChange={setPageSize}
                sortModel={sortModel}
                paginationMode="server"
                onSortModelChange={(newSortModel) => onSortModelChange(newSortModel)}
                sortingMode="server"
                columnVisibility
                columnSettings={settings}
                onColumnSettingsChange={(data) => onColumnSettingsChange(data)}
              />
            </Grid>
          )}
          {settings != null && ArchivedTableData.length > 0 && (
            <Grid xs={12} sx={{ backgroundColor: 'white' }} mt={1}>
              <WsDataGrid
                autoHeight
                columns={columns}
                rows={ArchivedTableData}
                onPageChange={setPage}
                pageSize={pageSize}
                page={page}
                onPageSizeChange={setPageSize}
                sortModel={sortModel}
                paginationMode="server"
                onSortModelChange={(newSortModel) => onSortModelChange(newSortModel)}
                sortingMode="server"
                columnVisibility
                columnSettings={settings}
                onColumnSettingsChange={(data) => onColumnSettingsChange(data)}
              />
            </Grid>
          )}
        </>
      ) : (
        <Box
          sx={{
            '& .MuiCard-root': {
              boxShadow: 'none',
              elevation: 0,
            },
          }}
        >
          {loader ? (
            <WsListingLoader />
          ) : (
            <NoDataFoundCard
              text={
                isAuthorizedAddInvoice
                  ? 'Click on +Add Fee arrangement to add Fee arrangements'
                  : 'No Fee Arrangement available for the client'
              }
              icon={<EmptyTeamsAndUsersIcon />}
              subElement={
                isAuthorizedAddInvoice ? (
                  <AddEntityButton
                    buttonText="Add Fee arrangement"
                    onClick={onAddButtonClick}
                    justifyContent="center"
                  />
                ) : (
                  <></>
                )
              }
            />
          )}
        </Box>
      )}
    </StyledGrid>
  );
};

export default ContractArrangementsTab;
